import React, { useEffect, useState } from "react";
import { Separator } from "components/separator/Separator";
import { Text, Flex, Button, Checkbox, TextField, Popover, IconButton } from "@radix-ui/themes";
import { Cross1Icon, MagnifyingGlassIcon, ChevronDownIcon } from "@radix-ui/react-icons";

export function LocationMultiSelect({ width = null, noShowBorder, options, onValueChange, defaultValue, disabled, displayTextType = 'locations' }) {
  
  const [selectedValues, setSelectedValues] = useState(defaultValue);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [dateDisplay, setDateDisplay] = useState("All locations");
  const [topCheckBoxBool, setTopCheckBoxBool] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Sync selectedValues with defaultValue when it changes
  useEffect(() => {
    if (defaultValue) {
      setSelectedValues(defaultValue);
    }
  }, [defaultValue]);

  // Update display text and log checkbox states when options or selectedValues change
  useEffect(() => {
    if (options && options.length > 0) {
      const currentValues = selectedValues || [];
      
      if (displayTextType === 'generic') {
        setDateDisplay(
          currentValues.length === 0
            ? "Select items"
            : currentValues.length === options.length
            ? "All selected"
            : `${currentValues.length} selected`
        );
      } else {
        setDateDisplay(
          currentValues.length === 0
            ? "Select locations"
            : currentValues.length === options.length
            ? "All locations"
            : `${currentValues.length} location${currentValues.length > 1 ? "s" : ""}`
        );
      }
    }
  }, [options, selectedValues, displayTextType]);

  const updateDisplayAndCheckboxes = (newSelectedValues) => {
    const values = Array.isArray(newSelectedValues) ? newSelectedValues : [];
    
    if (displayTextType === 'generic') {
      setDateDisplay(
        values.length === 0
          ? "Select items"
          : values.length === options.length
          ? "All selected"
          : `${values.length} selected`
      );
    } else {
      setDateDisplay(
        values.length === 0
          ? "Select locations"
          : values.length === options.length
          ? "All locations"
          : `${values.length} location${values.length > 1 ? "s" : ""}`
      );
    }
    setTopCheckBoxBool(values.length === options.length);
  };

  const handleClear = () => {
    setSelectedValues([]);
    onValueChange([]);
    updateDisplayAndCheckboxes([]);
  };

  const toggleAll = () => {
    if (selectedValues.length === options.length) {
      handleClear();
    } else {
      const allValues = options.map((option) => option.value);
      setSelectedValues(allValues);
      onValueChange(allValues);
      updateDisplayAndCheckboxes(allValues);
    }
  };

  const clearSearchQuery = () => {
    setSearchQuery("");
  };

  const filteredOptions = options
    .filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Popover.Root open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <Popover.Trigger asChild>
        <Button size="2"
          style={{
            cursor: `${disabled ? "not-allowed" : "default"}`,
            width: width,
            marginLeft: noShowBorder ? "3px" : null,
          }}
          variant={noShowBorder ? "ghost" : "soft"}
          color={selectedValues.length === 0 ? "red" : "gray"}
          onClick={() => setIsPopoverOpen(!isPopoverOpen)}
          disabled={disabled}>
          <Text color="gray" weight="medium">{dateDisplay}</Text>
          {width ? <Flex flexGrow="1" /> : null}
          <ChevronDownIcon style={{ color: "var(--gray-11)" }} />
        </Button>
      </Popover.Trigger>

      <Popover.Content maxHeight="400px" style={{ display: "flex", width: "296px", flexDirection: "column",
        alignItems: "start", padding: "0" }}>
        <Flex className="slot-container" style={{ alignSelf: "stretch" }} display="flex" direction="column" align="start">
          {/* Search Row */}
          <Flex className="search-row" gap="2" justify="start" align="center"
            style={{ alignSelf: "stretch", padding: "var(--space-2) 8px var(--space-2) 20px" }}>
            <Checkbox size="2" variant="surface" checked={topCheckBoxBool} onCheckedChange={toggleAll} />
            <TextField.Root style={{ flexGrow: "1" }} size="2" variant="soft" color="gray" placeholder="Search" 
              value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)}>
              <TextField.Slot>
                <MagnifyingGlassIcon height="16" width="16" />
              </TextField.Slot>
              <TextField.Slot>
                <IconButton size="1" variant="ghost" color="gray" onClick={clearSearchQuery}>
                  <Cross1Icon height="16" width="16" />
                </IconButton>
              </TextField.Slot>
            </TextField.Root>
          </Flex>
          <Separator color="gray" size="1" />
          {/* Options List */}
          <Flex className="location-list-container" style={{ alignSelf: "stretch" }} p="2" direction="column" justify="center" 
            align="start" gap="2">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                // Calculate checked state directly
                const isChecked = selectedValues && selectedValues.some((value) => String(value) === String(option.value));

                return (
                  <Flex gap="2"
                    key={option.value}
                    style={{
                      alignSelf: "stretch",
                      maxWidth: "100%",
                      height: "var(--space-6)",
                      padding: "0 var(--space-3)",
                      alignItems: "center",
                    }}
                  >
                    <Checkbox 
                      size="2"
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        let newValues;
                        if (checked) {
                          newValues = [...(selectedValues || []), option.value];
                        } else {
                          newValues = (selectedValues || []).filter((v) => String(v) !== String(option.value));
                        }
                        setSelectedValues(newValues);
                        onValueChange(newValues);
                        updateDisplayAndCheckboxes(newValues);
                      }}
                    />
                    <span
                      style={{
                        fontSize: "var(--font-size-2)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "280px",
                        display: "inline-block",
                        lineHeight: "var(--line-height-2)",
                        color: "var(--gray-12)",
                      }}
                    >
                      {option.label}
                    </span>
                  </Flex>
                );
              })
            ) : (
              <Text px="3">No Locations Found</Text>
            )}
          </Flex>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}
